// src/components/other/QuickAddCustomerModal.jsx
import { useEffect, useRef, useState } from "react";
import { useAddCustomerMutation } from "../../toolkitServices/customerApi";
import { motion } from "framer-motion";
import { indianStates } from "../../utils/helpers";
import toast from "react-hot-toast";

const QuickAddCustomerModal = ({ open, onClose, onAdded, selectedBranch }) => {
  const [createCustomer, { isLoading }] = useAddCustomerMutation();

  const initialForm = {
    customerId: "",
    customerName: "",
    permanentAddress: {
      addressLine1: "",
      city: "",
      pincode: "",
      state: "Maharashtra",
      country: "india",
    },
    deliveryAddress: {
      addressLine1: "",
      city: "",
      pincode: "",
      state: "Maharashtra",
      country: "india",
    },
  };

  const [form, setForm] = useState(initialForm);
  const nameRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setForm(initialForm);
    } else {
      setTimeout(() => nameRef.current?.focus(), 0);
    }
  }, [open]);

  if (!open) return null;

  const onlyDigits = (s) => (s || "").replace(/\D/g, "");

  const handleSameAsPermanent = () => {
    setForm((prev) => ({
      ...prev,
      deliveryAddress: {
        ...prev.permanentAddress,
      },
    }));
  };

  const onSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    const customerId = onlyDigits(form.customerId).slice(0, 10);
    if (!customerId || customerId.length < 10) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }

    const pa = form.permanentAddress || {};
    if (!pa.state) {
      toast.error("State is required");
      return;
    }

    if (pa.pincode && !/^\d{6}$/.test(pa.pincode)) {
      toast.error("Permanent pincode must be a 6-digit number");
      return;
    }

    if (
      form.deliveryAddress.pincode &&
      !/^\d{6}$/.test(form.deliveryAddress.pincode)
    ) {
      toast.error("Delivery pincode must be a 6-digit number");
      return;
    }

    const permanentAddress = {
      addressLine1: pa.addressLine1 || "",
      city: pa.city || "",
      pincode: pa.pincode || "",
      state: pa.state || "Maharashtra",
      country: pa.country || "india",
    };

    const da = form.deliveryAddress || {};
    const hasDelivery =
      (da.addressLine1 || "").trim() ||
      (da.city || "").trim() ||
      (da.pincode || "").trim();

    const deliveryAddresses = hasDelivery
      ? [
          {
            addressLine1: da.addressLine1 || "",
            city: da.city || "",
            pincode: da.pincode || "",
            state: da.state || "Maharashtra",
            country: da.country || "india",
            isDefault: true,
          },
        ]
      : [];

    const payload = {
      customerId,
      customerName: form.customerName || "",
      companyName: "",
      email: "",
      contact: "",
      adhar: "",
      pan: "",
      gst: "",
      shortName: "",
      rateOfInterest: "",
      creditPeriod: "",
      advanceAmount: "",
      balanceAmount: "",
      fineGold: "",
      fineSilver: "",
      isActive: true,

      permanentAddress,
      deliveryAddresses,

      addressLine1: permanentAddress.addressLine1,
      city: permanentAddress.city,
      pincode: permanentAddress.pincode,
      state: permanentAddress.state,
      country: permanentAddress.country,
    };

    if (selectedBranch?.value) {
      payload.branchId = selectedBranch.value;
      payload.branchName = selectedBranch.label || "";
    }

    try {
      const res = await createCustomer(payload).unwrap();
      const createdPayload = res?.data || res;
      const created = Array.isArray(createdPayload)
        ? createdPayload[0]
        : Array.isArray(createdPayload?.items)
          ? createdPayload.items[0]
          : createdPayload;

      if (!created?._id && !created?.id) {
        throw new Error("Customer was created but its ID was not returned");
      }

      toast.success("Customer added successfully");
      onAdded && onAdded(created);
    } catch (e) {
      const msg = e?.data?.msg || e?.error || "Failed to add customer";
      toast.error(msg);
    }
  };

  return (
    <div className="fixed inset-0 z-[999] bg-black/40 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-4"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold text-slate-800">
            Quick Add Customer
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="px-2 py-1 rounded-md border text-xs hover:bg-slate-50"
          >
            Close
          </button>
        </div>

        <form
          onSubmit={onSubmit}
          onKeyDownCapture={(e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
              e.preventDefault();
              e.stopPropagation();
              if (!isLoading) onSubmit(e);
            }
          }}
          className="space-y-4 max-h-[75vh] overflow-y-auto pr-1"
        >
          {/* BASIC DETAILS */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Customer ID (Mobile) <span className="text-red-500">*</span>
              </label>
              <input
                ref={nameRef}
                inputMode="numeric"
                maxLength={10}
                value={form.customerId}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    customerId: e.target.value.replace(/\D/g, ""),
                  }))
                }
                placeholder="10-digit mobile"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Customer Name
              </label>
              <input
                value={form.customerName}
                onChange={(e) =>
                  setForm((s) => ({ ...s, customerName: e.target.value }))
                }
                placeholder="Optional"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>

          {/* PERMANENT ADDRESS */}
          <div className="border rounded-lg p-3 bg-slate-50/50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700">
                Permanent Address
              </span>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Address Line 1
              </label>
              <input
                value={form.permanentAddress.addressLine1}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    permanentAddress: {
                      ...s.permanentAddress,
                      addressLine1: e.target.value,
                    },
                  }))
                }
                placeholder="Street / Area"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  City
                </label>
                <input
                  value={form.permanentAddress.city}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      permanentAddress: {
                        ...s.permanentAddress,
                        city: e.target.value,
                      },
                    }))
                  }
                  placeholder="City"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Pincode
                </label>
                <input
                  value={form.permanentAddress.pincode}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      permanentAddress: {
                        ...s.permanentAddress,
                        pincode: e.target.value.replace(/\D/g, ""),
                      },
                    }))
                  }
                  placeholder="6-digit"
                  maxLength={6}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.permanentAddress.state}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      permanentAddress: {
                        ...s.permanentAddress,
                        state: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Select State</option>
                  {indianStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Country
                </label>
                <select
                  value={form.permanentAddress.country}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      permanentAddress: {
                        ...s.permanentAddress,
                        country: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="india">India</option>
                </select>
              </div>
            </div>
          </div>

          {/* DELIVERY ADDRESS */}
          <div className="border rounded-lg p-3 bg-slate-50/50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700">
                Delivery Address (optional)
              </span>
              <button
                type="button"
                onClick={handleSameAsPermanent}
                className="text-[11px] px-2 py-1 rounded-md border border-violet-200 text-violet-700 hover:bg-violet-50"
              >
                Same as permanent
              </button>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Address Line 1
              </label>
              <input
                value={form.deliveryAddress.addressLine1}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    deliveryAddress: {
                      ...s.deliveryAddress,
                      addressLine1: e.target.value,
                    },
                  }))
                }
                placeholder="Street / Area"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  City
                </label>
                <input
                  value={form.deliveryAddress.city}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      deliveryAddress: {
                        ...s.deliveryAddress,
                        city: e.target.value,
                      },
                    }))
                  }
                  placeholder="City"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Pincode
                </label>
                <input
                  value={form.deliveryAddress.pincode}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      deliveryAddress: {
                        ...s.deliveryAddress,
                        pincode: e.target.value.replace(/\D/g, ""),
                      },
                    }))
                  }
                  placeholder="6-digit"
                  maxLength={6}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  State
                </label>
                <select
                  value={form.deliveryAddress.state}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      deliveryAddress: {
                        ...s.deliveryAddress,
                        state: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Select State</option>
                  {indianStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Country
                </label>
                <select
                  value={form.deliveryAddress.country}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      deliveryAddress: {
                        ...s.deliveryAddress,
                        country: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="india">India</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-md bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-3 py-1.5 rounded-md bg-[#6b59c6] text-white disabled:opacity-60"
            >
              {isLoading ? "Saving…" : "Save & Select"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default QuickAddCustomerModal;
